"use client";

import { useEffect, useState } from "react";
import type { Pricing_Tier } from "./types";
import { Api_Paths } from "@/lib/api/paths";
import { Auth_Fetch } from "@/lib/auth/client/session";
import {
  Radius_Miles_Default,
  Radius_Miles_Max,
  Radius_Miles_Min,
  Tier_Uses_Radius,
} from "@/lib/parcelq/radius";
import {
  ParcelQ_Btn_Primary_Class,
  ParcelQ_Btn_Secondary_Class,
  ParcelQ_Card_Class,
  ParcelQ_Card_Header_Class,
  ParcelQ_Heading_Class,
  ParcelQ_Input_Class,
  ParcelQ_Label_Mono_Class,
} from "./parcelq-styles";
import {
  County_Coverage_Get,
  Coverage_Level_Label,
} from "@/lib/parcelq/county-capabilities";

type Pricing_State_Props = {
  Pricing_OnBack: () => void;
  Pricing_OnSelect: (
    Tier: Pricing_Tier,
    Radius_Miles: number,
    Credits_To_Redeem: number,
  ) => void | Promise<void>;
  Is_Loading?: boolean;
  /** Estimated PM hours this lookup replaces (for Time Saved copy). */
  Time_Saved_Hours_Estimate?: number;
  /** County name for data-coverage disclosure. */
  County?: string | null;
};

const Pricing_Tiers: Pricing_Tier[] = [
  {
    Id: "feasibility",
    Title: "TTT Report",
    Reports:
      "Public due diligence packet — parcel identity, ownership & tax, zoning, hazards, recorded docs, Automatic Red Flags",
    Price: 45,
    Credits: 2,
    Hours_Saved: 4,
  },
  {
    Id: "radius-amenities",
    Title: "Amenity Report + Radius Map",
    Reports:
      "Interactive amenity layers · livability + affordable-housing proximity views · radius map · detail + summary",
    Price: 30,
    Credits: 1,
    Hours_Saved: 2,
  },
  {
    Id: "combo",
    Title: "Combination Report",
    Reports: "TTT Report + Amenity Report + Radius Map — full public packet",
    Price: 70,
    Credits: 4,
    Hours_Saved: 6,
  },
];

type Credits_Balance_Payload = {
  balance: number;
  expires_at: string | null;
  max_discount_ratio: number;
  dollar_per_credit: number;
};

function Credits_Label_Format(Count: number): string {
  return Count === 1 ? "1 credit" : `${Count} credits`;
}

function Price_Label_Format(Price_Due: number, Applied: number, List_Price: number): string {
  if (Applied <= 0) return `$${List_Price}`;
  const Needs_Cents = Price_Due % 1 !== 0;
  return `$${Price_Due.toFixed(Needs_Cents ? 2 : 0)}`;
}

export function Pricing_State({
  Pricing_OnBack,
  Pricing_OnSelect,
  Is_Loading = false,
  Time_Saved_Hours_Estimate = 5,
  County = null,
}: Pricing_State_Props) {
  const Coverage = County_Coverage_Get(County);
  const [Radius_Miles, Radius_Miles_Set] = useState(Radius_Miles_Default);
  const [Radius_Error, Radius_Error_Set] = useState<string | null>(null);
  const [Credits_Balance, Credits_Balance_Set] = useState<Credits_Balance_Payload | null>(null);
  const [Credits_Redeem, Credits_Redeem_Set] = useState(0);
  const [Purchasing_Tier_Id, Purchasing_Tier_Id_Set] = useState<string | null>(null);

  useEffect(() => {
    if (!Is_Loading) {
      Purchasing_Tier_Id_Set(null);
    }
  }, [Is_Loading]);

  useEffect(() => {
    let Cancelled = false;
    void (async () => {
      try {
        const Response = await Auth_Fetch(Api_Paths.V1.Credits);
        if (!Response.ok) {
          if (!Cancelled) Credits_Balance_Set(null);
          return;
        }
        const Payload = (await Response.json()) as Credits_Balance_Payload;
        if (!Cancelled) Credits_Balance_Set(Payload);
      } catch {
        if (!Cancelled) Credits_Balance_Set(null);
      }
    })();
    return () => {
      Cancelled = true;
    };
  }, []);

  function Radius_Handle_Change(Raw: string) {
    const Parsed = Number(Raw);
    Radius_Miles_Set(Number.isFinite(Parsed) ? Parsed : Radius_Miles_Default);
    Radius_Error_Set(null);
  }

  function Tier_Handle_Select(Tier: Pricing_Tier) {
    if (Tier_Uses_Radius(Tier.Id)) {
      if (!Number.isFinite(Radius_Miles) || Radius_Miles < Radius_Miles_Min || Radius_Miles > Radius_Miles_Max) {
        Radius_Error_Set(`Radius must be ${Radius_Miles_Min}–${Radius_Miles_Max} miles.`);
        return;
      }
    }
    Radius_Error_Set(null);
    Purchasing_Tier_Id_Set(Tier.Id);
    void Pricing_OnSelect(Tier, Radius_Miles, Credits_Redeem);
  }

  function Credits_Max_For_Tier(Tier: Pricing_Tier): number {
    if (!Credits_Balance?.balance) return 0;
    const Cap_Dollars = Math.floor(Tier.Price * (Credits_Balance.max_discount_ratio ?? 0.3));
    return Math.min(Credits_Balance.balance, Cap_Dollars);
  }

  return (
    <section className="flex flex-1 flex-col px-6 py-10">
      <div className={`mx-auto w-full max-w-4xl overflow-hidden rounded-2xl ${ParcelQ_Card_Class}`}>
        <div className={`${ParcelQ_Card_Header_Class} flex flex-wrap items-center justify-between gap-3 px-6`}>
          <div>
            <p className={ParcelQ_Label_Mono_Class}>Pay per report</p>
            <h2 className={`mt-1 text-xl sm:text-2xl ${ParcelQ_Heading_Class}`}>Choose a report bundle</h2>
          </div>
          <button type="button" onClick={Pricing_OnBack} className={ParcelQ_Btn_Secondary_Class}>
            Back
          </button>
        </div>

        <div className="border-b border-pq-border px-6 py-5">
          <p className="max-w-2xl text-sm leading-relaxed text-pq-fg-muted">
            Price reflects billable PM time you skip — opening dozens of county, GIS, tax, planning, and
            mapping tabs. Typical desk research for this packet: ~{Time_Saved_Hours_Estimate} hours.
          </p>
          {County ? (
            <div className="mt-3 rounded-lg border border-pq-border bg-slate-50 px-3 py-2.5">
              <p className={`${ParcelQ_Label_Mono_Class}`}>
                {Coverage.County} coverage
              </p>
              <p className="mt-1 text-xs leading-relaxed text-pq-fg-muted">
                Assessor {Coverage_Level_Label(Coverage.Layers.assessor)} · Tax{" "}
                {Coverage_Level_Label(Coverage.Layers.tax)} · Recorder{" "}
                {Coverage_Level_Label(Coverage.Layers.recorder)} · Zoning{" "}
                {Coverage_Level_Label(Coverage.Layers.deep_zoning)}
              </p>
              <p className="mt-1 text-xs text-pq-fg-subtle">{Coverage.Disclosure}</p>
            </div>
          ) : null}
          <p className={`mt-3 ${ParcelQ_Label_Mono_Class} text-amber-800`} role="status">
            Generation may take up to 5 minutes · keep this tab open
          </p>
        </div>

        <div className="grid border-b border-pq-border md:grid-cols-2 md:divide-x md:divide-slate-200">
          <div className="border-b border-pq-border px-6 py-5 md:border-b-0">
            <label htmlFor="amenities-radius" className={ParcelQ_Label_Mono_Class}>
              Amenities search radius
            </label>
            <div className="mt-3 flex items-center gap-3">
              <input
                id="amenities-radius"
                type="number"
                min={Radius_Miles_Min}
                max={Radius_Miles_Max}
                step={0.25}
                value={Radius_Miles}
                disabled={Is_Loading}
                onChange={(Event) => Radius_Handle_Change(Event.target.value)}
                className={`h-10 w-20 ${ParcelQ_Input_Class}`}
              />
              <span className="text-sm text-pq-fg-muted">mi</span>
              <input
                type="range"
                min={Radius_Miles_Min}
                max={Radius_Miles_Max}
                step={0.25}
                value={Math.min(Radius_Miles_Max, Math.max(Radius_Miles_Min, Radius_Miles))}
                disabled={Is_Loading}
                onChange={(Event) => Radius_Handle_Change(Event.target.value)}
                className="h-2 flex-1 accent-amber-600"
                aria-label="Amenities search radius slider"
              />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-pq-fg-subtle">
              Amenity + Combo only · max {Radius_Miles_Max} mi
            </p>
            {Radius_Error ? (
              <p className="mt-2 text-sm text-red-700" role="alert">
                {Radius_Error}
              </p>
            ) : null}
          </div>

          <div className="px-6 py-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className={ParcelQ_Label_Mono_Class}>Credits</p>
              {Credits_Balance ? (
                <p className="font-mono text-xs tabular-nums text-pq-fg-subtle">
                  <span className="text-base font-semibold text-pq-fg">{Credits_Balance.balance}</span>
                  {" "}available
                </p>
              ) : null}
            </div>
            {Credits_Balance ? (
              <>
                <div className="mt-3 flex items-center gap-3">
                  <input
                    id="credits-redeem"
                    type="number"
                    min={0}
                    max={Credits_Balance.balance}
                    step={1}
                    value={Credits_Redeem}
                    disabled={Is_Loading || Credits_Balance.balance <= 0}
                    onChange={(Event) =>
                      Credits_Redeem_Set(Math.max(0, Math.floor(Number(Event.target.value) || 0)))
                    }
                    aria-label="Credits to redeem on this purchase"
                    className={`h-10 w-20 ${ParcelQ_Input_Class}`}
                  />
                  <label htmlFor="credits-redeem" className="text-sm text-pq-fg-subtle">
                    redeem toward this purchase
                  </label>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-pq-fg-subtle">
                  $1 each · max 30% off · expire in 1 year
                </p>
                <p className="mt-1 text-xs leading-relaxed text-pq-fg-subtle">
                  Earn TTT 2 · Amenity 1 · Combo 4
                </p>
                <p className="mt-3 text-xs leading-relaxed text-pq-fg-muted">
                  Every site returns different public data. Credits are not returned if some
                  report sections are empty or incomplete — you are paying for research time saved.
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm leading-relaxed text-pq-fg-muted">
                Sign in to earn credits on purchases and redeem later ($1 each, max 30% off). Credits
                are not refunded when a report is missing some fields — coverage varies by site.
              </p>
            )}
          </div>
        </div>

        <ul className="divide-y divide-slate-200">
          {Pricing_Tiers.map((Tier) => {
            const Max_Redeem = Credits_Max_For_Tier(Tier);
            const Applied = Math.min(Credits_Redeem, Max_Redeem);
            const Price_Due = Math.max(0.5, Tier.Price - Applied);
            const Is_Combo = Tier.Id === "combo";
            const Credits_Earned = Tier.Credits ?? 0;
            const This_Purchasing = Is_Loading && Purchasing_Tier_Id === Tier.Id;

            return (
              <li
                key={Tier.Id}
                className={`flex flex-col gap-5 px-6 py-6 transition sm:flex-row sm:items-center sm:justify-between ${
                  Is_Combo ? "bg-pq-bg-soft/80" : "hover:bg-slate-50"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className={`${ParcelQ_Label_Mono_Class} ${Is_Combo ? "text-amber-800" : ""}`}>
                      {Tier.Title}
                    </p>
                    {Is_Combo ? (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-amber-700/80">
                        Best value
                      </span>
                    ) : null}
                    {Tier_Uses_Radius(Tier.Id) ? (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-pq-fg-subtle">
                        Radius {Radius_Miles} mi
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-pq-fg-muted">{Tier.Reports}</p>
                  <p className="mt-2 text-xs text-pq-fg-subtle">
                    ~{Tier.Hours_Saved ?? 3} PM hours saved · earns {Credits_Label_Format(Credits_Earned)}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-4 sm:flex-col sm:items-end sm:gap-3">
                  <div className="text-left sm:text-right">
                    <p className="text-2xl font-semibold tracking-tight tabular-nums text-pq-fg">
                      {Price_Label_Format(Price_Due, Applied, Tier.Price)}
                      {Applied > 0 ? (
                        <span className="ml-2 text-sm font-normal text-pq-fg-subtle line-through">
                          ${Tier.Price}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={Is_Loading}
                    onClick={() => Tier_Handle_Select(Tier)}
                    className={`${ParcelQ_Btn_Primary_Class} disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    {This_Purchasing ? "Purchasing…" : "Purchase"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
